/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, forwardRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { endpoints, axios } from 'Api';
import { Slider, Direction } from 'react-player-controls';
import { motion, MotionConfig } from 'framer-motion';
import { Pathname } from 'Routes';
import {
  getPlayerUiStyle,
  setPlayerUiStyle,
  PLAYER_UI_STYLE_OPTIONS,
  PLAYER_UI_STYLE_EVENT,
  PLAYER_UI_STYLE_KEY,
} from 'Config/featureFlags';
import { ShowNextVideo } from './components/show-next-video';
import {
  NfxPlay, NfxPause, NfxRewind, NfxForward, NfxVolume, NfxFullscreen, NfxSpeed, NfxBack,
} from './components/animated-icons';
import styles from './index.module.css';

// ─────────────────────────────────────────────────────────────────────
// Chrome ported from Components/NetflixPlayer (the player used by the
// gift "Tell Your Story" review step) so this page's controls read as the
// same product. Only the presentation moved — every prop, handler and the
// react-player-controls seek/hover-thumbnail plumbing below is unchanged.
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// Glyph normalisation.
//
// These inline icons were each drawn at whatever size looked right on its own,
// so their ink filled anywhere from 57% to 83% of the 24-unit box. At one CSS
// size that made the play glyph render ~30% smaller than the skip dials in the
// same row, and every skin smaller than the Netflix skin's Lucide set.
//
// `scaleTo` normalises them: each icon declares the factor (measured, not
// guessed) that brings its ink up to 83% of the box, so one `.nfIconBtn svg`
// size now yields the same 28px of ink everywhere — matching the Netflix skin.
// Stroke widths below are pre-divided by that factor, so scaling the glyph
// does not also fatten its lines.
// ─────────────────────────────────────────────────────────────────────
const scaleTo = (k) => (k === 1 ? undefined : `translate(12 12) scale(${k}) translate(-12 -12)`);

// Icons inlined so the control bar carries no asset dependency, drawn with
// the same rounded joins as the NetflixPlayer set.
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <g transform={scaleTo(1.35)}>
      <path
        d="M8.4 5.8v12.4L18.8 12 8.4 5.8z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1.78"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </g>
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <g transform={scaleTo(1.47)}>
      <rect x="7" y="5.2" width="3.4" height="13.6" rx="1.7" />
      <rect x="13.6" y="5.2" width="3.4" height="13.6" rx="1.7" />
    </g>
  </svg>
);
// This player skips by 15s (not 10s like the review player), so the glyphs
// carry a 15.
const Skip15BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
    <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">15</text>
  </svg>
);
const Skip15FwdIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 4v5h-5" />
    <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">15</text>
  </svg>
);
const VolumeIcon = ({ muted, level }) => {
  const speaker = <path d="M11 5.2 6.4 9H3.4v6h3l4.6 3.8V5.2z" fill="currentColor" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />;
  if (muted || level === 0) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.52" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <g transform={scaleTo(1.32)}>
          {speaker}
          <line x1="16.5" y1="9.5" x2="21.5" y2="14.5" />
          <line x1="21.5" y1="9.5" x2="16.5" y2="14.5" />
        </g>
      </svg>
    );
  }
  if (level < 0.5) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.52" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <g transform={scaleTo(1.32)}>
          {speaker}
          <path d="M15.6 9.4a3.6 3.6 0 0 1 0 5.2" />
        </g>
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.52" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <g transform={scaleTo(1.32)}>
        {speaker}
        <path d="M15.6 9.4a3.6 3.6 0 0 1 0 5.2" />
        <path d="M18.4 6.6a7.6 7.6 0 0 1 0 10.8" />
      </g>
    </svg>
  );
};
const FullscreenEnterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.92" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <g transform={scaleTo(1.04)}>
      <path d="M8.5 3.5H6A2.5 2.5 0 0 0 3.5 6v2.5" />
      <path d="M15.5 3.5H18A2.5 2.5 0 0 1 20.5 6v2.5" />
      <path d="M20.5 15.5V18a2.5 2.5 0 0 1-2.5 2.5h-2.5" />
      <path d="M3.5 15.5V18A2.5 2.5 0 0 0 6 20.5h2.5" />
    </g>
  </svg>
);
const FullscreenExitIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.92" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <g transform={scaleTo(1.04)}>
      <path d="M3.5 8.5H6A2.5 2.5 0 0 0 8.5 6V3.5" />
      <path d="M20.5 8.5H18A2.5 2.5 0 0 1 15.5 6V3.5" />
      <path d="M15.5 20.5V18a2.5 2.5 0 0 1 2.5-2.5h2.5" />
      <path d="M8.5 20.5V18A2.5 2.5 0 0 0 6 15.5H3.5" />
    </g>
  </svg>
);
const BackArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <g transform={scaleTo(1.22)}>
      <path d="M15 5 8 12l7 7" />
    </g>
  </svg>
);
const ReplayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
  </svg>
);

// The Studio skin uses double-chevron transport glyphs and a ring play
// button, and Capsule uses ±10s dials, so both carry their own set.
const FastBackIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <g transform={scaleTo(1.85)}>
      <path d="M11.5 6.6v10.8L4.2 12l7.3-5.4z" />
      <path d="M20.2 6.6v10.8L12.9 12l7.3-5.4z" />
    </g>
  </svg>
);
const FastFwdIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <g transform={scaleTo(1.85)}>
      <path d="M12.5 6.6v10.8L19.8 12l-7.3-5.4z" />
      <path d="M3.8 6.6v10.8L11.1 12 3.8 6.6z" />
    </g>
  </svg>
);
const Skip10BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
    <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">10</text>
  </svg>
);
const Skip10FwdIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 4v5h-5" />
    <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">10</text>
  </svg>
);

// Classic is a desktop-app console, so it uses hard-edged glyphs — the shared
// rounded pair reads as the YouTube set next to its flat square chrome.
const SharpPlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <g transform={scaleTo(1.32)}>
      <path d="M7 4.5 19.5 12 7 19.5V4.5z" />
    </g>
  </svg>
);
const SharpPauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <g transform={scaleTo(1.39)}>
      <rect x="6.6" y="4.8" width="3.8" height="14.4" />
      <rect x="13.6" y="4.8" width="3.8" height="14.4" />
    </g>
  </svg>
);

// Stand-in artwork for the Vision skin's now-playing card when the video has
// no sprite sheet to pull a frame from.
const FilmIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <path d="M3 9h18M3 15h18M8 4.5v15M16 4.5v15" />
  </svg>
);

const StyleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3.1" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// Skin → root modifier class. 'netflix' is the base look painted by .nfRoot
// itself, so it needs no extra class.
const SKIN_CLASS = {
  netflix: null,
  minimal: styles.skinMinimal,
  glass: styles.skinGlass,
  classic: styles.skinClassic,
  theater: styles.skinTheater,
  neon: styles.skinNeon,
  studio: styles.skinStudio,
  capsule: styles.skinCapsule,
  vision: styles.skinVision,
};

// Playback speeds cycled by the Capsule skin's rate button. The parent owns
// the value and feeds it to the video element, so this is a real control.
const PLAYBACK_RATES = [1, 1.25, 1.5, 2, 0.5];

export default forwardRef(
  ({
    onPlayPause,
    playing,
    onFastForward,
    onRewind,
    muted,
    onMute,
    onVolumeSeekUp,
    onVolumeChange,
    volumeActive,
    volume,
    onPlaybackRateChange,
    playbackRate,
    onToggleFullScreen,
    played,
    onSeek,
    onSeekMouseDown,
    onSeekMouseUp,
    elapsedTime,
    totalDuration,
    title,
    videoID,
    buffering,
    loadPercent = 0,
    loaded = 0,
    duration,
    vttContent,
    rows,
    columns,
    spriteImage,
    screenSize,
    format,
    showPlayAgainButton,
    setPlayAgainButton,
    showControllers,
    setControllers,
    hoverValue,
    setHoverValue,
    setState,
    state,
    nextVideoDetails,
    countDown,
    from,
    onBack,
  }, ref) => {
    const navigate = useNavigate();
    // Player skin (Config/featureFlags → PLAYER_UI_STYLES). Read once on mount,
    // then kept in sync with the picker on /content-layout — the setter fires a
    // window event, and `storage` covers a change made in another tab.
    const [uiStyle, setUiStyle] = useState(getPlayerUiStyle);
    const [styleMenuOpen, setStyleMenuOpen] = useState(false);
    const styleMenuRef = useRef(null);
    const [imgWidthPosition, setImgWidthPosition] = useState(0);
    const [imgHeightPosition, setImgHeightPosition] = useState(0);
    const [isDragStart, setDragStart] = useState(false);
    const [dragDirection, setDragDirection] = useState(undefined);
    const [progressValue, setProgressValue] = useState(undefined);
    // Same value as `dragDirection`, kept in a ref because the drag-end
    // resume below has to read it AFTER handleDragEnd has already cleared it.
    // React 18 batches the two setState calls in that (non-React, document-level)
    // mouseup handler into one render, so by the time the effect runs the
    // `dragDirection` state is already `undefined` — the resume branch never
    // matched and the video stayed paused after a seek.
    const dragDirectionRef = useRef(undefined);

    useEffect(() => {
      const apply = () => setUiStyle(getPlayerUiStyle());
      const onStorage = (e) => {
        if (!e.key || e.key === PLAYER_UI_STYLE_KEY) apply();
      };
      window.addEventListener(PLAYER_UI_STYLE_EVENT, apply);
      window.addEventListener('storage', onStorage);
      return () => {
        window.removeEventListener(PLAYER_UI_STYLE_EVENT, apply);
        window.removeEventListener('storage', onStorage);
      };
    }, []);

    // Close the skin menu on an outside click / Escape.
    useEffect(() => {
      if (!styleMenuOpen) return undefined;
      const onDown = (e) => {
        if (styleMenuRef.current && !styleMenuRef.current.contains(e.target)) {
          setStyleMenuOpen(false);
        }
      };
      const onKey = (e) => { if (e.key === 'Escape') setStyleMenuOpen(false); };
      // The page hides this whole overlay (visibility: hidden on the root) two
      // seconds after the last pointer move; with the menu open that would take
      // the menu with it, so hold the overlay visible until it closes.
      const root = document.getElementById('video-player-id');
      const keepVisible = () => {
        if (root && root.style.visibility === 'hidden') root.style.visibility = 'visible';
      };
      keepVisible();
      const keepAlive = setInterval(keepVisible, 400);
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
      return () => {
        clearInterval(keepAlive);
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('keydown', onKey);
      };
    }, [styleMenuOpen]);

    const mouseOutSlider = () => {
      if (hoverValue !== null) {
        setHoverValue(null);
      }
    };

    // Hover scrub preview: sprite thumbnail + timestamp, restyled to the
    // review player's tooltip (rounded dark card, hairline rim) but fed by
    // the same VTT sprite maths as before.
    //
    // Rendered by a plain function, NOT a nested component. A component
    // declared inside the render body is a brand-new type on every render, so
    // React unmounts and remounts its whole subtree — which, at one render per
    // mousemove, restarted the scrub handle's fade/scale transition constantly
    // and made the dot flicker while the pointer sat over the bar.
    const renderTimeTooltip = (numSeconds, style = {}) => (
      spriteImage ? (
        <div
          className={styles.nfThumbTooltip}
          style={style}
          onMouseEnter={() => { mouseOutSlider(); }}
        >
          <div
            className={styles.nfThumbImage}
            style={{
              background: `url(${spriteImage})`,
              backgroundSize: `${columns * 100}% ${rows * 100}%`,
              backgroundPosition: `-${imgWidthPosition}% -${imgHeightPosition}%`,
            }}
          />
          <span className={styles.nfThumbTime}>{format(numSeconds * duration)}</span>
        </div>
      ) : (
        <div className={cn(styles.nfThumbTooltip, styles.nfThumbTooltipBare)} style={style}>
          <span className={styles.nfThumbTime}>{format(numSeconds * duration)}</span>
        </div>
      )
    );

    const setDateTime = (str) => {
      const dateTime = new Date(new Date().getTime());
      const sp = str.split(':');
      dateTime.setHours(parseInt(sp[0], 10));
      dateTime.setMinutes(parseInt(sp[1], 10));
      dateTime.setSeconds(parseInt(sp[2], 10));
      return dateTime;
    };

    const getVttDataAsPerTime = (time) => {
      const index = vttContent?.findIndex((content) => setDateTime(content.start) <= setDateTime(time) && setDateTime(content.end) >= setDateTime(time));
      if (index && index !== -1) {
        return vttContent[index];
      }
    };

    const handleIntent = (value) => {
      if (value !== hoverValue) {
        setHoverValue(value);
      }
      if (isDragStart) {
        setProgressValue(value);
      }
      const hOverInSec = duration && value ? duration * value : 0;
      const timeformat = new Date(hOverInSec * 1000).toISOString().substr(11, 8);
      const data = getVttDataAsPerTime(timeformat);
      if (data?.x && data?.x !== '0') {
        setImgWidthPosition((parseInt(data.x, 10) / parseInt(data.w, 10)) * 100);
      } else {
        setImgWidthPosition(0);
      }
      if (data?.y && data?.y !== '0') {
        setImgHeightPosition((parseInt(data.y, 10) / parseInt(data.h, 10)) * 100);
      } else {
        setImgHeightPosition(0);
      }
    };

    const handleDragEnd = () => {
      setDragStart(false);
      setDragDirection(undefined);
    };
    const handleDragStart = (direction) => {
      dragDirectionRef.current = direction;
      setDragDirection(direction);
      setDragStart(true);
    };

    // Pause while scrubbing, resume once the pointer is released. Functional
    // updates so a progress tick landing mid-drag isn't clobbered by a stale
    // `state` snapshot.
    useEffect(() => {
      if (isDragStart) {
        if (dragDirectionRef.current === Direction.HORIZONTAL && state.playing) {
          setState((prev) => ({ ...prev, playing: false }));
        }
      } else if (dragDirectionRef.current === Direction.HORIZONTAL) {
        if (!state.playing) {
          setState((prev) => ({ ...prev, playing: true }));
        }
        dragDirectionRef.current = undefined;
      }
    }, [isDragStart]);

    useEffect(() => {
      if (isDragStart) {
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchend', handleDragEnd);
        return () => {
          document.removeEventListener('mouseup', handleDragEnd);
          document.removeEventListener('touchend', handleDragEnd);
        };
      }
    }, [isDragStart]);

    const moveListener = (event) => {
      const percentageMoved = parseInt(event.screenX, 10) / parseInt(window.screen.width, 10);
      setProgressValue(percentageMoved);
    };

    useEffect(() => {
      if (progressValue) {
        onSeekMouseUp(progressValue);
      }
    }, [progressValue]);

    useEffect(() => {
      if (isDragStart && dragDirection === Direction.HORIZONTAL) {
        const element = document.getElementById('video-player-id');
        element.addEventListener('mousemove', moveListener);
        return () => element.removeEventListener('mousemove', moveListener);
      }
    }, [isDragStart]);

    const handleIntentEnd = () => {
      setHoverValue(null);
    };

    // Seek bar — same react-player-controls Slider (so hover thumbnails,
    // drag-to-pause and click-to-seek all behave exactly as before); only the
    // track/played/buffered/thumb painting is new.
    const renderProgressBar = (value) => (
      <div
        id="slideBar"
        className={cn(styles.nfProgressRow, { [styles.nfScrubbing]: isDragStart })}
        onMouseLeave={() => { mouseOutSlider(); }}
        onBlur={() => { mouseOutSlider(); }}
        onMouseDown={() => { handleDragStart(Direction.HORIZONTAL); }}
        onMouseUp={handleDragEnd}
      >
        <Slider
          direction={Direction.HORIZONTAL}
          onChangeStart={(newValue) => { onSeekMouseUp(newValue); }}
          onChange={(newValue) => { onSeek(newValue); }}
          onIntent={handleIntent}
          onIntentEnd={handleIntentEnd}
          onIntentStart={handleIntentEnd}
          className={styles.nfSliderHit}
        >
          {hoverValue !== null ? (
            renderTimeTooltip(hoverValue, { left: `${hoverValue * 100}%` })
          ) : null}
          <div className={styles.nfProgressTrack}>
            {loaded > 0 ? (
              <div
                className={styles.nfProgressBuffered}
                style={{ width: `${Math.min(100, loaded * 100)}%` }}
                aria-hidden="true"
              />
            ) : null}
            <div className={styles.nfProgressPlayed} style={{ width: `${value}%` }} />
          </div>
          <div
            className={styles.nfProgressThumb}
            style={{ left: `${value}%` }}
            id="handler"
          />
        </Slider>
      </div>
    );

    const backToPreviewScreen = async () => {
      // Public share page (and other embeds) pass their own back handler so we
      // don't navigate into private, auth-only routes.
      if (onBack) {
        onBack();
        return;
      }
      let count = 0;
      if (localStorage?.getItem('video_id') && localStorage?.getItem('video_duration')) {
        if (!count) {
          await axios
            .post(endpoints.updateVideoPaused, { video_id: localStorage.getItem('video_id'), paused_at: localStorage.getItem('video_duration') })
            .then(() => {
              localStorage.setItem('video_id', null);
              localStorage.setItem('video_duration', null);
            })
            .catch(() => {
              localStorage.setItem('video_id', null);
              localStorage.setItem('video_duration', null);
            });
          count += 1;
        }
      }
      const fromGift = new URLSearchParams(window.location.search).get('fromGift');
      navigate(`${Pathname.getVideoPath(videoID)}${fromGift === 'true' ? '?fromGift=true' : ''}`);
    };

    const volLevel = muted ? 0 : volume;
    const showEndCard = showPlayAgainButton && played === 1;
    // Transport glyphs — hard-edged in Classic, the rounded pair everywhere
    // else. Used by both the centre disc and the control bar so one skin never
    // mixes the two sets.
    const isNetflix = uiStyle === 'netflix';
    const PlayGlyph = uiStyle === 'classic'
      ? SharpPlayIcon
      : isNetflix
        ? () => <NfxPlay size={30} />
        : PlayIcon;
    const PauseGlyph = uiStyle === 'classic'
      ? SharpPauseIcon
      : isNetflix
        ? () => <NfxPause size={30} />
        : PauseIcon;
    const ReplayGlyph = isNetflix
      ? () => <NfxRewind size={30} />
      : ReplayIcon;
    const hasNextVideo = !!(showPlayAgainButton && nextVideoDetails && Object.keys(nextVideoDetails).length);

    // ─────────────────────────────────────────────────────────────────
    // Shared pieces. Every skin below composes THESE — same handlers,
    // same react-player-controls Slider — into its own layout, so the
    // skins differ in structure (what is shown, where, and how it is
    // grouped), not just in colour.
    // ─────────────────────────────────────────────────────────────────
    const backBtn = (
      <button
        type="button"
        className={styles.nfBackBtn}
        onClick={() => { backToPreviewScreen(); }}
      >
        <BackArrowIcon />
        <span>Back</span>
      </button>
    );

    const titleEl = !buffering && title ? <div className={styles.nfTitle}>{title}</div> : null;

    // Skin switcher — the same list as the Preferences modal on
    // /content-layout, offered here so the choice can be made while watching.
    const styleSwitcher = (
      <div className={styles.nfStyleWrap} ref={styleMenuRef}>
        <button
          type="button"
          className={cn(styles.nfStyleBtn, { [styles.nfStyleBtnOpen]: styleMenuOpen })}
          onClick={() => { setStyleMenuOpen((o) => !o); }}
          aria-label="Player style"
          aria-haspopup="menu"
          aria-expanded={styleMenuOpen}
        >
          <StyleIcon />
        </button>
        {styleMenuOpen ? (
          <div className={styles.nfStyleMenu} role="menu">
            <div className={styles.nfStyleMenuHead}>Player style</div>
            {/* The list scrolls, not the card — masking the card itself to
                hint at more rows also dissolved its own border. */}
            <div className={styles.nfStyleList}>
              {PLAYER_UI_STYLE_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={uiStyle === opt.key}
                  className={cn(styles.nfStyleItem, { [styles.nfStyleItemActive]: uiStyle === opt.key })}
                  onClick={() => {
                    setPlayerUiStyle(opt.key);
                    setUiStyle(opt.key);
                    setStyleMenuOpen(false);
                  }}
                >
                  <span className={styles.nfStyleLabel}>{opt.label}</span>
                  <span className={styles.nfStyleDesc}>{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );

    const loaderEl = (() => {
      const LOADER_R = 54;
      const LOADER_CIRC = 2 * Math.PI * LOADER_R;
      const clampedPercent = Math.min(100, Math.max(0, loadPercent));
      return (
        <div className={styles.loaderWrap}>
          <svg className={styles.loaderSvg} viewBox="0 0 120 120">
            <defs>
              <linearGradient id="loaderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4a8ade" />
                <stop offset="60%" stopColor="#5aa5ef" />
                <stop offset="100%" stopColor="#8cc6ff" />
              </linearGradient>
            </defs>
            <circle className={styles.loaderTrack} cx="60" cy="60" r={LOADER_R} />
            <circle
              className={styles.loaderProgress}
              cx="60"
              cy="60"
              r={LOADER_R}
              strokeDasharray={LOADER_CIRC}
              strokeDashoffset={LOADER_CIRC * (1 - clampedPercent / 100)}
            />
          </svg>
          <div className={styles.loaderOrbit}>
            <span className={cn(styles.loaderDot, styles.loaderDot1)} />
            <span className={cn(styles.loaderDot, styles.loaderDot2)} />
            <span className={cn(styles.loaderDot, styles.loaderDot3)} />
          </div>
          <div className={styles.loaderCenter}>
            <div className={styles.loaderPercent}>{`${Math.round(clampedPercent)}%`}</div>
            <div className={styles.loaderLabel}>Loading</div>
          </div>
        </div>
      );
    })();

    const centerStage = (
      <div className={styles.nfCenter} onMouseEnter={() => { mouseOutSlider(); }}>
        {buffering ? loaderEl : showEndCard ? (
          <div className={styles.nfEndCard}>
            <button
              type="button"
              className={styles.nfCenterBtn}
              onClick={() => { setPlayAgainButton(false); }}
              aria-label="Play again"
            >
              <ReplayIcon />
            </button>
            <span className={styles.nfEndCardLabel}>Play Again</span>
          </div>
        ) : !playing ? (
          <button
            type="button"
            className={styles.nfCenterBtn}
            onClick={onPlayPause}
            aria-label="Play"
          >
            <PlayGlyph />
          </button>
        ) : null}
      </div>
    );

    const nextVideoRow = hasNextVideo ? (
      <div className={styles.nfNextVideoRow}>
        <ShowNextVideo nextVideoDetails={nextVideoDetails} countDown={countDown} from={from} />
      </div>
    ) : null;

    const playBtn = (
      <button
        type="button"
        className={cn(styles.nfIconBtn, styles.nfIconBtnPrimary)}
        onClick={onPlayPause}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <PauseGlyph /> : <PlayGlyph />}
      </button>
    );

    const rewindBtn = (
      <button type="button" className={styles.nfIconBtn} onClick={onRewind} aria-label="Back 15 seconds">
        <Skip15BackIcon />
      </button>
    );

    const forwardBtn = (
      <button type="button" className={styles.nfIconBtn} onClick={onFastForward} aria-label="Forward 15 seconds">
        <Skip15FwdIcon />
      </button>
    );

    const volumeCtl = (
      <div className={cn(styles.nfVolWrap, { [styles.nfVolWrapOpen]: volumeActive })}>
        <button
          type="button"
          className={styles.nfIconBtn}
          onClick={onMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          <VolumeIcon muted={muted} level={volLevel} />
        </button>
        <div
          className={styles.nfVolSlider}
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
            onVolumeSeekUp(ratio);
          }}
          role="slider"
          aria-label="Volume"
          aria-valuemin={0}
          aria-valuemax={1}
          aria-valuenow={volLevel}
          tabIndex={-1}
        >
          <div className={styles.nfVolFill} style={{ transform: `scaleX(${volLevel})` }} />
        </div>
      </div>
    );

    const fullscreenBtn = (
      <button
        type="button"
        className={styles.nfIconBtn}
        onClick={onToggleFullScreen}
        aria-label={screenSize ? 'Exit fullscreen' : 'Fullscreen'}
      >
        {screenSize ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
      </button>
    );

    // Combined "0:12 / 3:45" (Netflix/Minimal) and the split single values the
    // desk-style skins put at either end of the seek bar.
    const timeLabel = (
      <span className={styles.nfTimeLabel}>
        {elapsedTime}
        <span className={styles.nfTimeTotal}>{` / ${totalDuration}`}</span>
      </span>
    );
    const elapsedOnly = <span className={styles.nfTimeSolo}>{elapsedTime}</span>;
    const totalOnly = <span className={cn(styles.nfTimeSolo, styles.nfTimeSoloMuted)}>{totalDuration}</span>;

    // Studio's transport: the ring is the BUTTON's border, not part of the
    // glyph — drawn into the icon it forced the play/pause marks down to a
    // fraction of the size of the chevrons beside them.
    const ringPlayBtn = (
      <button
        type="button"
        className={cn(styles.nfIconBtn, styles.nfIconBtnPrimary, styles.stRingBtn)}
        onClick={onPlayPause}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <PauseGlyph /> : <PlayGlyph />}
      </button>
    );
    const fastBackBtn = (
      <button type="button" className={styles.nfIconBtn} onClick={onRewind} aria-label="Back 15 seconds">
        <FastBackIcon />
      </button>
    );
    const fastFwdBtn = (
      <button type="button" className={styles.nfIconBtn} onClick={onFastForward} aria-label="Forward 15 seconds">
        <FastFwdIcon />
      </button>
    );

    // Capsule's ±10-style dials (the handlers still seek by the page's 15s).
    const skipBackBtn = (
      <button type="button" className={styles.nfIconBtn} onClick={onRewind} aria-label="Rewind">
        <Skip10BackIcon />
      </button>
    );
    const skipFwdBtn = (
      <button type="button" className={styles.nfIconBtn} onClick={onFastForward} aria-label="Fast forward">
        <Skip10FwdIcon />
      </button>
    );

    // Playback speed — only offered when the host actually wired the handler.
    const rateBtn = onPlaybackRateChange ? (
      <button
        type="button"
        className={cn(styles.nfIconBtn, styles.nfRateBtn)}
        onClick={() => {
          const i = PLAYBACK_RATES.indexOf(playbackRate);
          onPlaybackRateChange(PLAYBACK_RATES[(i + 1) % PLAYBACK_RATES.length]);
        }}
        aria-label={`Playback speed ${playbackRate || 1}x`}
      >
        {`${playbackRate || 1}x`}
      </button>
    ) : null;

    const progressBar = renderProgressBar(played * 100);

    // ─────────────────────────────────────────────────────────────────
    // Layouts. One per skin — each arranges the pieces above differently.
    // ─────────────────────────────────────────────────────────────────

    // 1. NETFLIX — rebuilt against the real Netflix chrome: Lucide glyphs at
    //    reference size with no button containers, the seek bar carrying the
    //    remaining time at its right end, and the title centred in the button
    //    row between the transport (left) and the aux controls (right).
    //    Every glyph animates on hover/press — see .nfxBtn in the stylesheet.
    //    Only this skin uses the Lucide set; the others keep the inline SVGs.
    const nfxRemaining = (duration && format) ? format(duration * (1 - played)) : totalDuration;
    // Every button in this skin is a motion.button: framer-motion propagates
    // the "hover" / "tap" variant down to each stroke inside the icon, so the
    // glyph animates itself instead of the whole button being transformed.
    const nfxHover = { whileHover: 'hover', whileTap: 'tap' };

    const netflixLayout = (
      <MotionConfig reducedMotion="user">
        <div className={styles.nfxTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          <motion.button
            type="button"
            className={cn(styles.nfxBtn, styles.nfxBackBtn)}
            onClick={() => { backToPreviewScreen(); }}
            aria-label="Back"
            {...nfxHover}
          >
            <NfxBack size={36} />
          </motion.button>
          <div className={styles.nfSpacer} />
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.nfBottom}>
          {nextVideoRow}
          {!buffering ? (
            <div className={styles.nfxControls} onMouseLeave={() => { mouseOutSlider(); }}>
              <div className={styles.nfxSeekRow}>
                <div className={styles.nfxSeek}>{progressBar}</div>
                <span className={styles.nfxRemaining}>{nfxRemaining}</span>
              </div>

              <div className={styles.nfxBtnRow} onMouseEnter={() => { mouseOutSlider(); }}>
                <div className={styles.nfxCluster}>
                  <motion.button
                    type="button"
                    className={styles.nfxBtn}
                    onClick={onPlayPause}
                    aria-label={playing ? 'Pause' : 'Play'}
                    {...nfxHover}
                  >
                    {playing ? <NfxPause size={34} /> : <NfxPlay size={34} />}
                  </motion.button>

                  {/* The seek amount rides inside the dial, as in the
                      reference — the arc winds, the number stays put. */}
                  <motion.button
                    type="button"
                    className={cn(styles.nfxBtn, styles.nfxSkipBack)}
                    onClick={onRewind}
                    aria-label="Back 15 seconds"
                    {...nfxHover}
                  >
                    <NfxRewind size={32} />
                    <span className={styles.nfxSkipNum}>15</span>
                  </motion.button>
                  <motion.button
                    type="button"
                    className={cn(styles.nfxBtn, styles.nfxSkipFwd)}
                    onClick={onFastForward}
                    aria-label="Forward 15 seconds"
                    {...nfxHover}
                  >
                    <NfxForward size={32} />
                    <span className={styles.nfxSkipNum}>15</span>
                  </motion.button>

                  <div className={cn(styles.nfVolWrap, styles.nfxVolWrap, { [styles.nfVolWrapOpen]: volumeActive })}>
                    <motion.button
                      type="button"
                      className={styles.nfxBtn}
                      onClick={onMute}
                      aria-label={muted ? 'Unmute' : 'Mute'}
                      {...nfxHover}
                    >
                      <NfxVolume size={36} muted={muted} level={volLevel} />
                    </motion.button>
                    <div
                      className={styles.nfVolSlider}
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
                        onVolumeSeekUp(ratio);
                      }}
                      role="slider"
                      aria-label="Volume"
                      aria-valuemin={0}
                      aria-valuemax={1}
                      aria-valuenow={volLevel}
                      tabIndex={-1}
                    >
                      <div className={styles.nfVolFill} style={{ transform: `scaleX(${volLevel})` }} />
                    </div>
                  </div>
                </div>

                <div className={styles.nfxTitle}>{title}</div>

                <div className={cn(styles.nfxCluster, styles.nfxClusterEnd)}>
                  {onPlaybackRateChange ? (
                    <motion.button
                      type="button"
                      className={cn(styles.nfxBtn, styles.nfxSpeedBtn)}
                      onClick={() => {
                        const i = PLAYBACK_RATES.indexOf(playbackRate);
                        onPlaybackRateChange(PLAYBACK_RATES[(i + 1) % PLAYBACK_RATES.length]);
                      }}
                      aria-label={`Playback speed ${playbackRate || 1}x`}
                      {...nfxHover}
                    >
                      <NfxSpeed size={39} />
                      <span className={styles.nfxSpeedTag}>{`${playbackRate || 1}x`}</span>
                    </motion.button>
                  ) : null}
                  <motion.button
                    type="button"
                    className={styles.nfxBtn}
                    onClick={onToggleFullScreen}
                    aria-label={screenSize ? 'Exit fullscreen' : 'Fullscreen'}
                    {...nfxHover}
                  >
                    <NfxFullscreen size={35} active={!!screenSize} />
                  </motion.button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </MotionConfig>
    );

    // 2. MINIMAL — no bars and no scrims: the seek bar sits inset above a
    //    single floating row — white pill play with the skips beside it, then
    //    the title and time, and the aux controls at the far right.
    const minimalLayout = (
      <>
        <div className={styles.minTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          <div className={styles.nfSpacer} />
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.minBottom}>
          {nextVideoRow}
          {!buffering ? (
            <>
              <div className={styles.minSeek} onMouseLeave={() => { mouseOutSlider(); }}>
                {progressBar}
              </div>
              <div className={styles.minFloatRow} onMouseEnter={() => { mouseOutSlider(); }}>
                {playBtn}
                {rewindBtn}
                {forwardBtn}
                <div className={styles.minMeta}>
                  {titleEl}
                  {timeLabel}
                </div>
                <div className={styles.nfSpacer} />
                {rateBtn}
                {volumeCtl}
                {fullscreenBtn}
              </div>
            </>
          ) : null}
        </div>
      </>
    );

    // 3. GLASS — one floating capsule dock, centred and inset from the edges,
    //    with the seek bar INLINE between the transport and the aux controls
    //    (no separate progress row) and the times at either end of it.
    const glassLayout = (
      <>
        <div className={styles.glassTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          {titleEl}
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.glassBottom}>
          {nextVideoRow}
          {!buffering ? (
            <div className={styles.glassDock} onMouseLeave={() => { mouseOutSlider(); }}>
              <div className={styles.glassTransport} onMouseEnter={() => { mouseOutSlider(); }}>
                {rewindBtn}
                {playBtn}
                {forwardBtn}
              </div>
              {elapsedOnly}
              <div className={styles.glassSeek}>{progressBar}</div>
              {totalOnly}
              <div className={styles.glassDivider} />
              <div className={styles.glassAux} onMouseEnter={() => { mouseOutSlider(); }}>
                {rateBtn}
                {volumeCtl}
                {fullscreenBtn}
              </div>
            </div>
          ) : null}
        </div>
      </>
    );

    // 4. CLASSIC — desktop-app console: an opaque title strip at the top and
    //    an opaque panel at the bottom whose seek row is flanked by the two
    //    time readouts, over a three-column button row (volume left,
    //    transport centred, fullscreen right).
    const classicLayout = (
      <>
        <div className={styles.clsTitleBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          {titleEl}
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.clsBottom}>
          {nextVideoRow}
          {!buffering ? (
            <div className={styles.clsPanel} onMouseLeave={() => { mouseOutSlider(); }}>
              <div className={styles.clsSeekRow}>
                {elapsedOnly}
                <div className={styles.clsSeek}>{progressBar}</div>
                {totalOnly}
              </div>
              <div className={styles.clsBtnRow} onMouseEnter={() => { mouseOutSlider(); }}>
                <div className={styles.clsSide}>{volumeCtl}</div>
                <div className={styles.clsTransport}>
                  {rewindBtn}
                  {playBtn}
                  {forwardBtn}
                </div>
                <div className={cn(styles.clsSide, styles.clsSideEnd)}>
                  {rateBtn}
                  {fullscreenBtn}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </>
    );

    // 5. THEATER — cinema chrome: solid letterbox bars, a centred marquee
    //    title, and the transport lifted OFF the bar into a floating console
    //    over the picture; the bar itself carries nothing but the seek line
    //    and its times.
    const theaterLayout = (
      <>
        <div className={styles.thTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          <div className={styles.thMarquee}>{titleEl}</div>
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.thBottom}>
          {nextVideoRow}
          {!buffering ? (
            <>
              <div className={styles.thConsoleWrap}>
                <div className={styles.thConsole} onMouseEnter={() => { mouseOutSlider(); }}>
                  {rewindBtn}
                  {playBtn}
                  {forwardBtn}
                  <div className={styles.thDivider} />
                  {rateBtn}
                  {volumeCtl}
                  {fullscreenBtn}
                </div>
              </div>
              <div className={styles.thSeekBar} onMouseLeave={() => { mouseOutSlider(); }}>
                {elapsedOnly}
                <div className={styles.thSeek}>{progressBar}</div>
                {totalOnly}
              </div>
            </>
          ) : null}
        </div>
      </>
    );

    // 6. NEON — game-HUD, all along the bottom: the transport sits in a lit
    //    cluster on the left, the readout is a chip centred between the two
    //    clusters, the aux cluster closes the right, and the seek line glows
    //    along the very bottom edge of the frame. No panel behind it.
    const neonLayout = (
      <>
        <div className={styles.neonTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          {titleEl}
          <div className={styles.nfSpacer} />
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.neonBottom}>
          {nextVideoRow}
          {!buffering ? (
            <>
              <div className={styles.neonBar} onMouseEnter={() => { mouseOutSlider(); }}>
                <div className={styles.neonCluster}>
                  {playBtn}
                  {rewindBtn}
                  {forwardBtn}
                </div>
                <div className={styles.neonHud}>{timeLabel}</div>
                <div className={cn(styles.neonCluster, styles.neonClusterEnd)}>
                  {rateBtn}
                  {volumeCtl}
                  {fullscreenBtn}
                </div>
              </div>
              <div className={styles.neonEdgeSeek} onMouseLeave={() => { mouseOutSlider(); }}>
                {progressBar}
              </div>
            </>
          ) : null}
        </div>
      </>
    );

    // 7. STUDIO — one wide scrim bar: the volume slider stays open at the far
    //    left, the transport is centred and flanked by the elapsed and total
    //    times around a ring play button, and fullscreen closes the right.
    //    Seek bar spans the bar above them.
    const studioLayout = (
      <>
        <div className={styles.stTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          {titleEl}
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.stBottom}>
          {nextVideoRow}
          {!buffering ? (
            <div className={styles.stBar} onMouseLeave={() => { mouseOutSlider(); }}>
              <div className={styles.stSeek}>{progressBar}</div>
              <div className={styles.stRow} onMouseEnter={() => { mouseOutSlider(); }}>
                <div className={styles.stSide}>{volumeCtl}</div>
                <div className={styles.stTransport}>
                  {elapsedOnly}
                  {fastBackBtn}
                  {ringPlayBtn}
                  {fastFwdBtn}
                  {totalOnly}
                </div>
                <div className={cn(styles.stSide, styles.stSideEnd)}>
                  {rateBtn}
                  {fullscreenBtn}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </>
    );

    // 8. CAPSULE — a full-width hairline seek with a bar-shaped handle, and
    //    under it two rounded pill groups: play / skip / time on the left,
    //    volume, speed and fullscreen on the right. Nothing spans the middle,
    //    so the picture stays open.
    const capsuleLayout = (
      <>
        <div className={styles.capTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          {titleEl}
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.capBottom}>
          {nextVideoRow}
          {!buffering ? (
            <>
              <div className={styles.capSeek} onMouseLeave={() => { mouseOutSlider(); }}>
                {progressBar}
              </div>
              <div className={styles.capRow} onMouseEnter={() => { mouseOutSlider(); }}>
                <div className={styles.capPill}>
                  {playBtn}
                  {skipBackBtn}
                  {skipFwdBtn}
                  <span className={styles.capTime}>
                    {elapsedTime}
                    <span className={styles.nfTimeTotal}>{` / ${totalDuration}`}</span>
                  </span>
                </div>
                <div className={styles.nfSpacer} />
                <div className={styles.capPill}>
                  {volumeCtl}
                  {rateBtn}
                  {fullscreenBtn}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </>
    );

    // 9. VISION — one wide frosted bar floating clear of the bottom edge:
    //    transport on the left, a now-playing card in the middle carrying the
    //    artwork, title, time and the seek line along its foot, and the aux
    //    controls on the right.
    const visionLayout = (
      <>
        <div className={styles.visTopBar} onMouseEnter={() => { mouseOutSlider(); }}>
          {backBtn}
          {styleSwitcher}
        </div>
        {centerStage}
        <div className={styles.visBottom}>
          {nextVideoRow}
          {!buffering ? (
            <div className={styles.visBarWrap}>
              <div className={styles.visBar} onMouseLeave={() => { mouseOutSlider(); }}>
                <div className={styles.visTransport} onMouseEnter={() => { mouseOutSlider(); }}>
                  {rewindBtn}
                  {playBtn}
                  {forwardBtn}
                </div>

                <div className={styles.visCard}>
                  {/* Artwork + text on one row, the seek on its own row under
                      it — stacked rather than overlaid, so the artwork can
                      never sit on top of the bar. */}
                  <div className={styles.visCardTop}>
                    <div className={styles.visArt} aria-hidden="true">
                      <FilmIcon />
                    </div>
                    <div className={styles.visMeta}>
                      <div className={styles.visCardTitle}>{title || 'Now playing'}</div>
                      <div className={styles.visCardSub}>
                        {elapsedTime}
                        <span className={styles.nfTimeTotal}>{` / ${totalDuration}`}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.visCardSeek}>{progressBar}</div>
                </div>

                <div className={styles.visAux} onMouseEnter={() => { mouseOutSlider(); }}>
                  {rateBtn}
                  {volumeCtl}
                  {fullscreenBtn}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </>
    );

    const LAYOUTS = {
      netflix: netflixLayout,
      minimal: minimalLayout,
      glass: glassLayout,
      classic: classicLayout,
      theater: theaterLayout,
      neon: neonLayout,
      studio: studioLayout,
      capsule: capsuleLayout,
      vision: visionLayout,
    };

    return (
      <div
        className={cn(styles.nfRoot, SKIN_CLASS[uiStyle], { [styles.nfRootDim]: showPlayAgainButton || isDragStart })}
        ref={ref}
        id="video-player-id"
      >
        {LAYOUTS[uiStyle] || LAYOUTS.netflix}
      </div>
    );
  }
);
